import { useCallback, useState } from 'react';

/** Identity accessor for an item in an include/exclude list. */
type GetId<T> = (item: T) => number | null | undefined;

/**
 * Pure: add `item` to `target` (idempotent by id) and drop it from `other`, so it ends up in at most one
 * list. Exclusion-wins semantics are the caller's choice of which list is `target`.
 */
export function addExclusive<T>(target: T[], other: T[], item: T, getId: GetId<T>): { target: T[]; other: T[] } {
	const id = getId(item);
	return {
		target: target.some(x => getId(x) === id) ? target : [...target, item],
		other: other.filter(x => getId(x) !== id)
	};
}

/**
 * Pure: replace `target` with `items` wholesale (e.g. a multi-select selection) and prune any of those ids
 * from `other`, keeping the two lists disjoint.
 */
export function setListExclusive<T>(items: T[], other: T[], getId: GetId<T>): { target: T[]; other: T[] } {
	const ids = new Set(items.map(getId));
	return { target: items, other: other.filter(x => !ids.has(getId(x))) };
}

/** Pure: remove the item with `id` from `list`. */
export function removeById<T>(list: T[], id: number | null | undefined, getId: GetId<T>): T[] {
	return list.filter(x => getId(x) !== id);
}

export interface IncludeExcludeLists<T> {
	include: T[];
	exclude: T[];
	/** Reset both lists (e.g. from a freshly-loaded server state). */
	setBoth: (include: T[], exclude: T[]) => void;
	/** Add a single item to include, removing it from exclude. */
	addInclude: (item: T) => void;
	/** Add a single item to exclude, removing it from include. */
	addExclude: (item: T) => void;
	/** Replace the whole include list, pruning those ids from exclude. */
	setIncludeList: (items: T[]) => void;
	/** Replace the whole exclude list, pruning those ids from include. */
	setExcludeList: (items: T[]) => void;
	removeInclude: (id: number | null | undefined) => void;
	removeExclude: (id: number | null | undefined) => void;
}

/**
 * State for two mutually-exclusive lists (force-include / force-exclude): any item placed in one list is
 * removed from the other, so it is never in both. Generic over the item type; identity is `getId`. Used by
 * the customer-group and group-member override dialogs (single-add search vs whole-list multi-select).
 */
export function useIncludeExcludeLists<T>(getId: GetId<T>): IncludeExcludeLists<T> {
	const [include, setInclude] = useState<T[]>([]);
	const [exclude, setExclude] = useState<T[]>([]);

	const setBoth = useCallback((inc: T[], exc: T[]) => {
		setInclude(inc);
		setExclude(exc);
	}, []);

	const addInclude = useCallback(
		(item: T) => {
			const id = getId(item);
			setInclude(prev => (prev.some(x => getId(x) === id) ? prev : [...prev, item]));
			setExclude(prev => prev.filter(x => getId(x) !== id));
		},
		[getId]
	);

	const addExclude = useCallback(
		(item: T) => {
			const id = getId(item);
			setExclude(prev => (prev.some(x => getId(x) === id) ? prev : [...prev, item]));
			setInclude(prev => prev.filter(x => getId(x) !== id));
		},
		[getId]
	);

	const setIncludeList = useCallback(
		(items: T[]) => {
			const ids = new Set(items.map(getId));
			setInclude(items);
			setExclude(prev => prev.filter(x => !ids.has(getId(x))));
		},
		[getId]
	);

	const setExcludeList = useCallback(
		(items: T[]) => {
			const ids = new Set(items.map(getId));
			setExclude(items);
			setInclude(prev => prev.filter(x => !ids.has(getId(x))));
		},
		[getId]
	);

	const removeInclude = useCallback((id: number | null | undefined) => setInclude(prev => removeById(prev, id, getId)), [getId]);
	const removeExclude = useCallback((id: number | null | undefined) => setExclude(prev => removeById(prev, id, getId)), [getId]);

	return { include, exclude, setBoth, addInclude, addExclude, setIncludeList, setExcludeList, removeInclude, removeExclude };
}
